import { z } from 'zod';

import { AreaIdSchema } from './area.schema';
import { BenchmarkSymbolSchema } from './benchmark-ohlc.schema';
import { CompareSetSchema, compareSetInput } from './compare.schema';
import {
  AreaTaxonomySchema,
  ApiSortDirSchema,
  DEFAULT_AREA_TAXONOMY,
  DEFAULT_TAXONOMY,
  SortDirSchema,
  type FilterKey,
  type SortDir,
  type SortKey,
  type WindowKey,
  WindowKeySchema,
  FilterKeySchema,
  SortKeySchema,
  TaxonomySchema,
} from './enums';

/* `apps/web` needs these to strip default-valued params from a shared link and
   to render active sort state, so they are public rather than module-private. */
export const DEFAULT_WINDOW: WindowKey = '1Y';
export const DEFAULT_FILTER: FilterKey = 'ALL';
export const DEFAULT_SORT_A = Object.freeze({ k: 'ret', d: -1 }) as {
  readonly k: SortKey;
  readonly d: SortDir;
};
export const DEFAULT_SORT_B = Object.freeze({ k: 'risk', d: 1 }) as {
  readonly k: SortKey;
  readonly d: SortDir;
};

/** Dashboard-only overlay flag. Presence of `1` is expanded; omitted is collapsed. */
export const TREEMAP_EXPAND_ON = 1 as const;
export type TreemapExpandParam = typeof TREEMAP_EXPAND_ON;

/**
 * Manual-book filter (ADR-0042 · #84). Presence of `1` narrows a screen to owned
 * names; omitted is the whole universe.
 *
 * Spelled `book` on BOTH vocabularies, which is not a violation of the
 * URL-vs-API trap. That trap protects `win`/`idx`/`sortA` — abbreviations D3 put
 * into shared links permanently, whose long forms (`window`/`index`/`sort`) the
 * API wanted instead. `book` has no abbreviation to diverge from, and inventing
 * one (`bk`) would buy a second name for one concept and nothing else.
 *
 * A FLAG, not the book itself: the lots never travel in the URL. A shared link
 * that carried `book=AAPL,MSFT` would publish someone's holdings into browser
 * history, referrer headers and any chat client it was pasted into.
 */
export const BOOK_FILTER_ON = 1 as const;
export type BookFilterParam = typeof BOOK_FILTER_ON;

/**
 * JSON search params coerce a bare `book=1` to the number `1`. A string `'1'`
 * would be written as `book="1"` and would miss every hand-typed deep link.
 * Accept both on read; the write path emits the number so the URL stays `book=1`.
 */
function bookFilterInput(value: unknown): unknown {
  return value === 1 || value === '1' ? BOOK_FILTER_ON : value;
}

/**
 * SPA URL: anything but `1`/`"1"` soft-drops to "unfiltered", the same way
 * `exp` and `range` do. A `.default(BOOK_FILTER_ON)` would make the whole
 * universe inexpressible and pin every in-place write to the book.
 */
const BookSearchParamSchema = z.preprocess(
  bookFilterInput,
  z.literal(BOOK_FILTER_ON).optional().catch(undefined),
);

/**
 * Compare set (ADR-0044 · #83). Spelled `cmp` on the URL and `compare` on the
 * API — the same short-vs-long split as `win`/`window`. No `.catch()`: a
 * malformed token or an over-long list must not soft-drop to the whole
 * universe (that is the silently-wrong reading ADR-0012 rejects).
 */
const CompareSearchParamSchema = z.preprocess(compareSetInput, CompareSetSchema.optional());

/**
 * JSON search params coerce a bare `exp=1` to the number `1`. A string `'1'`
 * would be written as `exp="1"` and would miss every hand-typed deep link.
 * Accept both on read; the write path emits the number so the URL stays `exp=1`.
 */
function treemapExpandInput(value: unknown): unknown {
  return value === 1 || value === '1' ? TREEMAP_EXPAND_ON : value;
}

/** SPA URL: unknown enum → product default (FR-A2 deep links). */
const WindowSearchParamSchema = WindowKeySchema.default(DEFAULT_WINDOW).catch(DEFAULT_WINDOW);
const FilterSearchParamSchema = FilterKeySchema.default(DEFAULT_FILTER).catch(DEFAULT_FILTER);
const TaxonomySearchParamSchema = TaxonomySchema.default(DEFAULT_TAXONOMY).catch(DEFAULT_TAXONOMY);
const AreaTaxonomySearchParamSchema =
  AreaTaxonomySchema.default(DEFAULT_AREA_TAXONOMY).catch(DEFAULT_AREA_TAXONOMY);

/**
 * Neutral sort state (no baked-in catch). Apply .default().catch() at the
 * field site so sortA and sortB keep independent fallbacks.
 */
export const SortStateSchema = z.object({
  k: SortKeySchema,
  d: z.preprocess((v) => (typeof v === 'string' && v.trim() !== '' ? Number(v) : v), SortDirSchema),
});
export type SortState = z.infer<typeof SortStateSchema>;

/* Thunks, not the shared literal: `.catch(obj)` hands back the SAME object on
   every invalid parse, so one mutation of a caught result would rewrite the
   module default for the whole process — and `.default()` clones from that same
   live object, corrupting both arms. */
const sortDefault = (base: { readonly k: SortKey; readonly d: SortDir }) => (): SortState => ({
  ...base,
});

/**
 * SPA URL search params for `/` (dashboard). Short names on purpose (ADR-0003);
 * mapping to API long names happens once in `apps/web/src/data/queries/*`.
 */
export const DashboardSearchParamsSchema = z.object({
  win: WindowSearchParamSchema,
  idx: FilterSearchParamSchema,
  /* Parsed and defaulted here in PR1; no `apps/web` route reads it until the
     industry grouping ships. Present now so a link shared from a later build
     round-trips through this one instead of being dropped. */
  tax: TaxonomySearchParamSchema,
  /* Dashboard overlay only. Omitted = collapsed; anything but `1`/`"1"`
     soft-drops the same way `range` does on stock — a default of `1` would
     make the collapsed state inexpressible and pin every in-place write. Hops
     off `/` use writtenNavSearch, which does not carry `exp`, so a sector/stock
     URL cannot pin a dashboard viewing mode. */
  exp: z.preprocess(treemapExpandInput, z.literal(TREEMAP_EXPAND_ON).optional().catch(undefined)),
  book: BookSearchParamSchema,
  /* Capped ticker list. Omitted is the whole (index-filtered) universe. */
  cmp: CompareSearchParamSchema,
  sortA: SortStateSchema.default(sortDefault(DEFAULT_SORT_A)).catch(sortDefault(DEFAULT_SORT_A)),
  sortB: SortStateSchema.default(sortDefault(DEFAULT_SORT_B)).catch(sortDefault(DEFAULT_SORT_B)),
});
export type DashboardSearchParams = z.infer<typeof DashboardSearchParamsSchema>;

/** SPA URL search params for `/sector/$sectorId`. */
export const SectorSearchParamsSchema = z.object({
  win: WindowSearchParamSchema,
  idx: FilterSearchParamSchema,
  book: BookSearchParamSchema,
  cmp: CompareSearchParamSchema,
});
export type SectorSearchParams = z.infer<typeof SectorSearchParamsSchema>;

/** SPA URL search params for `/area/$areaId`; v1 areas are industries. */
export const AreaSearchParamsSchema = z.object({
  win: WindowSearchParamSchema,
  idx: FilterSearchParamSchema,
  tax: AreaTaxonomySearchParamSchema,
  book: BookSearchParamSchema,
  cmp: CompareSearchParamSchema,
});
export type AreaSearchParams = z.infer<typeof AreaSearchParamsSchema>;

/**
 * SPA URL search params for `/stock/$ticker`.
 *
 * `range` is chart-local (F12) and OPTIONAL: unset means "follow the global
 * window", which the reference implementation expresses as `S.range || w`
 * (demo:633, :1004). Defaulting it to a concrete window would make the unset
 * state inexpressible and render `?win=3Y` as a 3Y header over a 1Y chart.
 * Resolve as `range ?? win` at the route/hook.
 */
export const StockSearchParamsSchema = z.object({
  win: WindowSearchParamSchema,
  idx: FilterSearchParamSchema,
  /* Carried for the same reason `idx` is: a hop from a book-filtered dashboard
     into a stock and back must land on the book-filtered dashboard. It is a
     FILTER the user is navigating under, not a dashboard viewing mode like
     `exp`, which is deliberately left behind on a hop. The stock screen's own
     owned-marker does not read it — that is a client-side join on `/v1/book`,
     so a name is marked as held whether or not the filter is engaged. */
  book: BookSearchParamSchema,
  /* Carried so a hop from `/?cmp=AAPL,MSFT` into a stock and back lands on
     the same research set. The stock screen does not filter by it. */
  cmp: CompareSearchParamSchema,
  range: WindowKeySchema.optional().catch(undefined),
  tax: TaxonomySchema.optional().catch(undefined),
  area: AreaIdSchema.optional().catch(undefined),
});
export type StockSearchParams = z.infer<typeof StockSearchParamsSchema>;

/** Chart Terminal overlay flag. Presence of `0` is off; omitted is on. */
export const MA_OVERLAY_OFF = 0 as const;
export type MaOverlayParam = typeof MA_OVERLAY_OFF;

/**
 * JSON search params coerce a bare `ma=0` to the number `0`. A string `'0'`
 * would be written as `ma="0"` and would miss every hand-typed deep link.
 * Accept both on read; the write path emits the number so the URL stays `ma=0`.
 */
function maOverlayInput(value: unknown): unknown {
  return value === 0 || value === '0' ? MA_OVERLAY_OFF : value;
}

/**
 * SPA URL search params for `/chart/$ticker`.
 *
 * Same vocabulary as stock, plus optional `ma=0` to hide moving-average
 * overlays (omitted = on). Anything but `0`/`"0"` soft-drops the same way
 * `range` does — a default of `1` would make the on state inexpressible and
 * pin every in-place write. Hops off `/chart` use `chartStudySearch`, which
 * does not carry `ma`, so a stock/technical URL cannot pin a chart viewing mode.
 */
export const ChartSearchParamsSchema = StockSearchParamsSchema.extend({
  ma: z.preprocess(maOverlayInput, z.literal(MA_OVERLAY_OFF).optional().catch(undefined)),
});
export type ChartSearchParams = z.infer<typeof ChartSearchParamsSchema>;

export const DEFAULT_API_LIMIT = 50;
/**
 * Must be >= the largest universe the plan admits, or the §6.6 sort-key parity
 * test (`limit=<universeSize>&sort=risk`) fails once the universe passes it.
 */
export const API_LIMIT_MAX = 2_500;
export const API_Q_MAX = 128;

/**
 * API query vocabulary (plan §6.6 / naming §4.2): long names only.
 * Never accept `win`/`idx` here — that mapping is the web query-hook layer.
 *
 * Uses `.default()` for omitted keys (ETag canonicalQuery) but **no** `.catch()`:
 * hand-crafted invalid values → parse failure → 400 VALIDATION_FAILED (data-flow §1.1).
 * SPA deep-link soft defaults live only on URL search-param schemas above.
 */
const apiQueryFields = {
  window: WindowKeySchema.default(DEFAULT_WINDOW),
  index: FilterKeySchema.default(DEFAULT_FILTER),
  sort: SortKeySchema.default('ret'),
  dir: ApiSortDirSchema.default('desc'),
  limit: z.coerce.number().int().positive().max(API_LIMIT_MAX).default(DEFAULT_API_LIMIT),
  q: z.string().max(API_Q_MAX).default(''),
  /**
   * Manual-book filter (ADR-0042). `.optional()` rather than `.default(false)`
   * ON PURPOSE: `canonicalQuery` is the parsed object with keys sorted, and it
   * keys the ETag `routeKey`. A default would put a `book` key into EVERY
   * canonical query, changing the ETag of every existing read for clients that
   * never asked for the book. Optional means an omitted `book` canonicalises
   * byte-identically to today, so only requests that actually engage the filter
   * mint a new ETag.
   *
   * No `.catch()`, per the rule above: `?book=2` is a hand-crafted invalid
   * value and must be a 400 VALIDATION_FAILED, not a silent unfiltered screen.
   */
  book: z.preprocess(bookFilterInput, z.literal(BOOK_FILTER_ON).optional()),
  /**
   * Compare set (ADR-0044). `.optional()` rather than `.default([])` for the
   * same ETag reason as `book`: a default would put `compare` into every
   * canonical query and move every existing read's validator.
   */
  compare: z.preprocess(compareSetInput, CompareSetSchema.optional()),
} as const;

/**
 * Per-route query schemas (plan §6.6 assigns a distinct set per route).
 *
 * These are not cosmetic: `canonicalQuery` is the parsed object with keys sorted,
 * and it keys BOTH the ETag `routeKey` and the Query cache. A single permissive
 * schema lets `/v1/dashboard?sort=mcap` parse and mint a DIFFERENT ETag for a
 * byte-identical body — and injects a `limit` into a payload that is documented
 * as the full filtered set, not paginated.
 */
export const DashboardQuerySchema = z.object({
  window: apiQueryFields.window,
  index: apiQueryFields.index,
  book: apiQueryFields.book,
  compare: apiQueryFields.compare,
});
export type DashboardQuery = z.infer<typeof DashboardQuerySchema>;

/** Optional book + compare extras shared by query keys and hooks (max-params). */
export type UniverseFilter = Pick<DashboardQuery, 'book' | 'compare'>;

/**
 * Exact-optional spread for repository query objects. Omitted keys stay
 * absent so `exactOptionalPropertyTypes` callers do not write `book:
 * undefined` into a payload whose wire form is "key missing".
 */
export function universeQueryFields(filters?: UniverseFilter): UniverseFilter {
  return {
    ...(filters?.book === undefined ? {} : { book: filters.book }),
    ...(filters?.compare === undefined ? {} : { compare: filters.compare }),
  };
}

/** `/v1/sectors` and `/v1/sectors/:id`. */
export const SectorQuerySchema = DashboardQuerySchema;
export type SectorQuery = z.infer<typeof SectorQuerySchema>;

/** `/v1/areas` and `/v1/areas/:id`; unsupported taxonomies are validation errors. */
export const AreaQuerySchema = z.object({
  window: apiQueryFields.window,
  index: apiQueryFields.index,
  book: apiQueryFields.book,
  compare: apiQueryFields.compare,
  taxonomy: AreaTaxonomySchema.default(DEFAULT_AREA_TAXONOMY),
});
export type AreaQuery = z.infer<typeof AreaQuerySchema>;

/** `/v1/securities` — the only sorted, limited collection route. */
export const SecuritiesQuerySchema = z.object({
  window: apiQueryFields.window,
  index: apiQueryFields.index,
  book: apiQueryFields.book,
  compare: apiQueryFields.compare,
  sort: apiQueryFields.sort,
  dir: apiQueryFields.dir,
  limit: apiQueryFields.limit,
});
export type SecuritiesQuery = z.infer<typeof SecuritiesQuerySchema>;

/** `/v1/search`. */
export const SearchQuerySchema = z.object({
  q: apiQueryFields.q,
  limit: apiQueryFields.limit,
});
export type SearchQuery = z.infer<typeof SearchQuerySchema>;

/** `/v1/securities/:ticker` and `/v1/securities/:ticker/{series,peers,diversifiers}`. */
export const SeriesQuerySchema = z.object({ window: apiQueryFields.window });
export type SeriesQuery = z.infer<typeof SeriesQuerySchema>;

/**
 * `/v1/benchmark/ohlc` (§6.6 · §1.11 hop 7). `symbol` is REQUIRED and carries no
 * `.default()`: hop 7 specifies "an explicit, Zod-validated symbol selector",
 * and a default would answer for a caller that named no symbol on the one route
 * whose whole purpose is choosing between benchmarks. A symbol outside
 * `BENCHMARK_SYMBOLS` is `400 VALIDATION_FAILED`; a symbol inside it that is not
 * yet admitted to `market.benchmark_served` is a `200` `not_in_served_set`, so
 * this schema must not be confused with the served set.
 */
export const BenchmarkOhlcQuerySchema = z.object({
  symbol: BenchmarkSymbolSchema,
  window: apiQueryFields.window,
});
export type BenchmarkOhlcQuery = z.infer<typeof BenchmarkOhlcQuerySchema>;

/**
 * Union of every API query field. Retained for callers that need the full
 * vocabulary; prefer the per-route schema above so the ETag key set matches
 * the route's actual inputs.
 */
export const ApiQueryParamsSchema = z.object(apiQueryFields);
export type ApiQueryParams = z.infer<typeof ApiQueryParamsSchema>;
